import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AtendimentoNavbar } from '../components/Shared/AtendimentoNavbar';
import { AtendimentoSidebar } from '../components/Shared/AtendimentoSidebar';
import { AtendimentoDashboardPage } from '../pages/Atendimento/AtendimentoDashboardPage';
import { AtendimentoInboxPage } from '../pages/Atendimento/AtendimentoInboxPage';
import { ContactsListPage } from '../pages/Atendimento/ContactsListPage';
import { ContactDetailPage } from '../pages/Atendimento/ContactDetailPage';
import { InboxesListPage } from '../pages/Atendimento/InboxesListPage';
import { InboxWizardPage } from '../pages/Atendimento/InboxWizardPage';
import { AgentBotsPage } from '../pages/Atendimento/AgentBotsPage';
import { AgentBotFormPage } from '../pages/Atendimento/AgentBotFormPage';
import { SupportGroupsPage } from '../pages/Atendimento/SupportGroupsPage';
import { LabelsPage } from '../pages/Atendimento/LabelsPage';
import { CannedResponsesPage } from '../pages/Atendimento/CannedResponsesPage';
import { SupportSettingsPage } from '../pages/Atendimento/SupportSettingsPage';
import { OperationalIndicatorsPage } from '../pages/Atendimento/OperationalIndicatorsPage';
import { UserSignaturePage } from '../pages/Atendimento/UserSignaturePage';
import { WorkspaceConsolePage } from '../pages/Atendimento/Workspace/WorkspaceConsolePage';
import { HELPDESK_BASE, HELPDESK_VALID_ROUTE_PREFIXES, helpdeskRoutes } from '../routes/helpdeskRoutes';
import { useHelpdeskHomePath } from '../hooks/useHelpdeskHomePath';

const ConversationRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { homePath } = useHelpdeskHomePath();

  if (!id)
  {
    return <Navigate to={homePath} replace />;
  }

  return <Navigate to={helpdeskRoutes.workspaceConversation(id)} replace />;
};

const AtendimentoFallbackRedirect: React.FC = () => {
  const { homePath } = useHelpdeskHomePath();
  return <Navigate to={homePath} replace />;
};

const AtendimentoLayout: React.FC = () => {
  const location = useLocation();
  const { homePath } = useHelpdeskHomePath();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isWorkspaceSurface = location.pathname === helpdeskRoutes.workspace;
  const isInboxWizard =
    location.pathname === helpdeskRoutes.settingsInboxNew
    || /\/settings\/inboxes\/[^/]+\/edit$/.test(location.pathname);
  const isDashboard = location.pathname === HELPDESK_BASE || location.pathname === `${HELPDESK_BASE}/`;

  const isInvalidRoute = !HELPDESK_VALID_ROUTE_PREFIXES.some(route => location.pathname.startsWith(route));

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (isInvalidRoute && !isDashboard)
  {
    return <Navigate to={homePath} replace />;
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-gray-50">
      <AtendimentoNavbar onOpenMobileNav={() => setMobileNavOpen(true)} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <AtendimentoSidebar
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
        <main
          className={`flex-1 min-h-0 ${
            isWorkspaceSurface
              ? 'overflow-hidden flex flex-col'
              : isInboxWizard
                ? 'overflow-hidden flex flex-col p-4 lg:p-6'
                : 'overflow-y-auto p-4 lg:p-6'
          }`}
        >
          <Routes>
            <Route path="/" element={<AtendimentoDashboardPage />} />
            <Route path="/workspace" element={<WorkspaceConsolePage />} />
            <Route path="/workspace/settings" element={<Navigate to={helpdeskRoutes.settingsInboxes} replace />} />
            <Route path="/settings" element={<Navigate to={helpdeskRoutes.settingsInboxes} replace />} />
            <Route path="/inbox" element={<AtendimentoInboxPage />} />
            <Route path="/conversations" element={<Navigate to={helpdeskRoutes.workspace} replace />} />
            <Route path="/conversations/:id" element={<ConversationRedirect />} />
            <Route path="/contacts" element={<ContactsListPage />} />
            <Route path="/contacts/:id" element={<ContactDetailPage />} />
            <Route path="/settings/inboxes" element={<InboxesListPage />} />
            <Route path="/settings/inboxes/new" element={<InboxWizardPage />} />
            <Route path="/settings/inboxes/:id/edit" element={<InboxWizardPage />} />
            <Route path="/settings/email-signature" element={<UserSignaturePage />} />
            <Route path="/settings/bots" element={<AgentBotsPage />} />
            <Route path="/settings/bots/new" element={<AgentBotFormPage />} />
            <Route path="/settings/bots/:id/edit" element={<AgentBotFormPage />} />
            <Route path="/settings/groups" element={<SupportGroupsPage />} />
            <Route path="/settings/labels" element={<LabelsPage />} />
            <Route path="/settings/canned-responses" element={<CannedResponsesPage />} />
            <Route path="/settings/indicators" element={<OperationalIndicatorsPage />} />
            <Route path="/settings/notifications" element={<Navigate to={helpdeskRoutes.settingsInboxes} replace />} />
            <Route path="/settings/general" element={<SupportSettingsPage />} />
            <Route path="*" element={<AtendimentoFallbackRedirect />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AtendimentoLayout;
